/* ============================================================
   PublicOfferView.js
   Public single-offer page — fully redesigned layout.
   All original data/logic kept. Only layout & style changed.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,    // RTL back arrow (points right = backward in Arabic)
  MapPin,
  Maximize,
  Bed,
  Bath,
  Phone,
  Home,
  Loader2,
  ChevronLeft,   // gallery prev (RTL: move left = go forward visually)
  ChevronRight,  // gallery next (RTL: move right = go backward visually)
  Image as ImageIcon,
  Tag,
  Calendar,      // property age
  Building2,     // floors
  ArrowUpDown,   // street width
  FileText,      // deed type
  Compass,       // direction/facing
} from 'lucide-react';
import { formatArea, formatPrice, getPropertyTypeColor } from '../utils/helpers';
import { buildOfferShareText, buildOfferShareUrl } from '../utils/offerShareUtils';
// Dedicated CSS for this page (new file)
import './PublicOfferView.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';

/* ── Helper: collect all images from offer into one array ── */
const getAllImages = (offer) => {
  if (!offer) return [];
  const imgs = [];
  // put cover image first if it exists
  if (offer.coverImage) imgs.push(offer.coverImage);
  // add remaining images, skip duplicates of coverImage
  if (Array.isArray(offer.images)) {
    offer.images.forEach((img) => {
      if (img && img !== offer.coverImage) imgs.push(img);
    });
  }
  return imgs;
};

/* ── Helper: property type label in Arabic ── */
const getPropertyTypeLabel = (type) => {
  const map = {
    apartment: 'شقة', villa: 'فيلا', land: 'أرض',
    office: 'مكتب', warehouse: 'مستودع', building: 'عمارة',
    floor: 'دور', duplex: 'دوبلكس', chalet: 'شاليه',
    farm: 'مزرعة', shop: 'محل',
  };
  return map[type] || type || 'عقار';
};

/* ═══════════════════════════════════════════
   Sub-component: Image Gallery / Slider
   Handles multiple images with prev/next and
   a counter badge.
═══════════════════════════════════════════ */
const Gallery = ({ images }) => {
  const [current, setCurrent] = useState(0);

  // No images → show placeholder
  if (!images || images.length === 0) {
    return (
      <div className="pov-gallery">
        <div className="pov-gallery-placeholder">
          <ImageIcon size={48} />
          <span>لا توجد صور</span>
        </div>
      </div>
    );
  }

  const total = images.length;

  /* Go to next image — wraps around */
  const goNext = () => setCurrent((prev) => (prev + 1) % total);

  /* Go to previous image — wraps around */
  const goPrev = () => setCurrent((prev) => (prev - 1 + total) % total);

  return (
    <div className="pov-gallery">
      {/* Main image */}
      <img
        className="pov-gallery-img"
        src={images[current]}
        alt={`صورة ${current + 1}`}
        draggable={false}
      />

      {/* Arrow buttons — only shown when more than 1 image */}
      {total > 1 && (
        <>
          {/* In RTL: right button = go to previous */}
          <button className="pov-gallery-btn prev" onClick={goPrev} aria-label="السابق">
            <ChevronRight size={20} />
          </button>
          {/* In RTL: left button = go to next */}
          <button className="pov-gallery-btn next" onClick={goNext} aria-label="التالي">
            <ChevronLeft size={20} />
          </button>
        </>
      )}

      {/* Counter badge: "current / total" */}
      {total > 1 && (
        <div className="pov-gallery-counter">
          {current + 1} / {total}
        </div>
      )}

      {/* Dot indicators — only shown when more than 1 image */}
      {total > 1 && (
        <div className="pov-gallery-dots">
          {images.map((_, idx) => (
            <button
              key={idx}
              className={`pov-gallery-dot ${idx === current ? 'active' : ''}`}
              onClick={() => setCurrent(idx)}
              aria-label={`صورة ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   Main page component
═══════════════════════════════════════════ */
const PublicOfferView = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();

  // ── State (unchanged from original) ──
  const [loading, setLoading] = useState(true);
  const [offer, setOffer]     = useState(null);
  const [error, setError]     = useState('');

  // ── Fetch offer (unchanged from original) ──
  useEffect(() => {
    const fetchOffer = async () => {
      if (!offerId) {
        setError('العرض غير متاح');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError('');
        const response = await fetch(`${API_BASE_URL}/public/offers/${offerId}`);
        const data = await response.json();

        if (data.success && data.data?.offer) {
          setOffer(data.data.offer);
        } else {
          setError('العرض غير متاح حالياً');
        }
      } catch (_) {
        setError('تعذر تحميل العرض');
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
  }, [offerId]);

  // ── Derive owner phone (unchanged from original) ──
  const ownerPhone = useMemo(() => {
    const raw = offer?.ownerPhone || offer?.user?.phone;
    return raw ? String(raw) : '';
  }, [offer]);

  // ── WhatsApp handler (unchanged from original) ──
  const handleWhatsApp = () => {
    if (!ownerPhone) return;
    let phone = ownerPhone.replace(/[\s-]/g, '');
    if (phone.startsWith('0')) phone = `966${phone.slice(1)}`;
    else if (!phone.startsWith('966') && !phone.startsWith('+966')) phone = `966${phone}`;
    phone = phone.replace('+', '');

    const text =
      `مرحباً، أرغب بالاستفسار عن العرض:\n` +
      `${offer?.title || 'عرض عقاري'}\n` +
      `${offer?.city || ''}${offer?.district ? ` - ${offer.district}` : ''}`;
    const offerUrl = buildOfferShareUrl({ offerId: offer?._id || offerId });
    const message = buildOfferShareText({ text, url: offerUrl });
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // ── Collect all gallery images ──
  const images = useMemo(() => getAllImages(offer), [offer]);

  /* ── Loading screen ── */
  if (loading) {
    return (
      <div className="pov-loading">
        <Loader2 className="pov-spinner" size={40} />
        <span>جاري تحميل العرض...</span>
      </div>
    );
  }

  /* ── Error / not-found screen ── */
  if (!offer) {
    return (
      <div className="pov-error">
        <Home size={56} color="#d1d5db" />
        <h2>{error || 'العرض غير متاح'}</h2>
        <button className="pov-error-btn" onClick={() => navigate('/')}>
          العودة للرئيسية
        </button>
      </div>
    );
  }

  /* ══════════════════════════════════════════
     MAIN PAGE RENDER
  ══════════════════════════════════════════ */
  return (
    <div className="pov-page">

      {/* ── Sticky top bar with back button ── */}
      <div className="pov-topbar">
        <button className="pov-back-btn" onClick={() => navigate(-1)}>
          <ArrowRight size={18} />
          رجوع
        </button>
      </div>

      <div className="pov-content">

        {/* ══════════════════════════════════
            SECTION 1 — IMAGE GALLERY
            Shows cover + all images with
            prev/next arrows and counter.
        ════════════════════════════════== */}
        <Gallery images={images} />

        {/* ══════════════════════════════════
            SECTION 2 — UNIFIED DETAILS CARD
            Title, location, price, and ALL
            spec fields in one single card.
            offerNature (صفة العرض) is hidden
            on this page intentionally.
        ════════════════════════════════== */}
        <div className="pov-card">
          {/* Badges: property type + offer type (بيع/إيجار) */}
          <div className="pov-badges">
            {(offer.propertyType || offer.type) && (
              <span className="pov-badge pov-badge-type">
                {getPropertyTypeLabel(offer.propertyType || offer.type)}
              </span>
            )}
            {offer.offerType && (
              <span
                className={`pov-badge ${offer.offerType === 'sale' ? 'pov-badge-sale' : 'pov-badge-rent'}`}
              >
                {offer.offerType === 'sale' ? 'بيع' : 'إيجار'}
              </span>
            )}
          </div>

          {/* Title */}
          {offer.title && <p className="pov-title">{offer.title}</p>}

          {/* Location */}
          {(offer.city || offer.district) && (
            <div className="pov-location">
              <MapPin size={15} />
              <span>
                {offer.city || ''}
                {offer.city && offer.district ? ' — ' : ''}
                {offer.district || ''}
              </span>
            </div>
          )}

          {/* Price — large and prominent above the grid */}
          {offer.price > 0 && (
            <div className="pov-price">{formatPrice(offer.price)}</div>
          )}

          {/* ── Unified 3-column specs grid ──
              Every field is optional — only shown when it has a value.
              offerNature is excluded from this page. */}
          <div className="pov-specs-grid">

            {/* Area */}
            {offer.area > 0 && (
              <div className="pov-spec-item">
                <Maximize size={20} />
                <span className="pov-spec-label">المساحة</span>
                <span className="pov-spec-value">{formatArea(offer.area)}</span>
              </div>
            )}

            {/* Rooms */}
            {offer.rooms > 0 && (
              <div className="pov-spec-item">
                <Bed size={20} />
                <span className="pov-spec-label">الغرف</span>
                <span className="pov-spec-value">{offer.rooms}</span>
              </div>
            )}

            {/* Bathrooms */}
            {offer.bathrooms > 0 && (
              <div className="pov-spec-item">
                <Bath size={20} />
                <span className="pov-spec-label">الحمامات</span>
                <span className="pov-spec-value">{offer.bathrooms}</span>
              </div>
            )}

            {/* Property age */}
            {offer.age > 0 && (
              <div className="pov-spec-item">
                <Calendar size={20} />
                <span className="pov-spec-label">عمر العقار</span>
                <span className="pov-spec-value">{offer.age} سنة</span>
              </div>
            )}

            {/* Floors */}
            {offer.floors > 0 && (
              <div className="pov-spec-item">
                <Building2 size={20} />
                <span className="pov-spec-label">الأدوار</span>
                <span className="pov-spec-value">{offer.floors}</span>
              </div>
            )}

            {/* Street width */}
            {offer.streetWidth > 0 && (
              <div className="pov-spec-item">
                <ArrowUpDown size={20} />
                <span className="pov-spec-label">عرض الشارع</span>
                <span className="pov-spec-value">{offer.streetWidth} م</span>
              </div>
            )}

            {/* Deed type — translate value to Arabic */}
            {offer.deedType && (
              <div className="pov-spec-item">
                <FileText size={20} />
                <span className="pov-spec-label">نوع الصك</span>
                <span className="pov-spec-value">
                  {offer.deedType === 'free' ? 'حر' : 'مرهون'}
                </span>
              </div>
            )}

            {/* Direction / facing — translate value to Arabic */}
            {offer.direction && (
              <div className="pov-spec-item">
                <Compass size={20} />
                <span className="pov-spec-label">الواجهة</span>
                <span className="pov-spec-value">
                  {offer.direction === 'north'     ? 'شمال'        :
                   offer.direction === 'south'     ? 'جنوب'        :
                   offer.direction === 'east'      ? 'شرق'         :
                   offer.direction === 'west'      ? 'غرب'         :
                   offer.direction === 'northeast' ? 'شمال شرقي'   :
                   offer.direction === 'northwest' ? 'شمال غربي'   :
                   offer.direction === 'southeast' ? 'جنوب شرقي'   :
                   offer.direction === 'southwest' ? 'جنوب غربي'   :
                   offer.direction}
                </span>
              </div>
            )}

            {/* Offer type (بيع/إيجار) as a grid cell — only shown if price is missing */}
            {offer.offerType && !offer.price && (
              <div className="pov-spec-item">
                <Tag size={20} />
                <span className="pov-spec-label">نوع العرض</span>
                <span className="pov-spec-value">
                  {offer.offerType === 'sale' ? 'بيع' : 'إيجار'}
                </span>
              </div>
            )}

          </div>
        </div>

        {/* ══════════════════════════════════
            SECTION 3 — DESCRIPTION
            Only rendered when offer has text.
        ════════════════════════════════== */}
        {offer.description && (
          <div className="pov-card">
            <h2>الوصف</h2>
            <p className="pov-description">{offer.description}</p>
          </div>
        )}

        {/* ══════════════════════════════════
            SECTION 5 — SIMILAR OFFERS
            Placeholder only — will be wired
            to the API in a future step.
        ════════════════════════════════== */}
        <div className="pov-card">
          <h2>عروض مشابهة</h2>
          <div className="pov-similar-placeholder">
            سيتم عرض العروض المشابهة هنا قريباً
          </div>
        </div>

      </div>{/* end .pov-content */}

      {/* ══════════════════════════════════
          SECTION 7 — STICKY CONTACT BAR
          Fixed at bottom of screen.
          Shows Call + WhatsApp buttons.
          Hidden when no phone available.
      ════════════════════════════════== */}
      {ownerPhone && (
        <div className="pov-contact-bar">
          {/* Call button */}
          <a className="pov-btn-call" href={`tel:${ownerPhone}`}>
            <Phone size={18} />
            اتصال
          </a>

          {/* WhatsApp button */}
          <button className="pov-btn-whatsapp" onClick={handleWhatsApp}>
            {/* WhatsApp icon via SVG (no external dependency) */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            واتساب
          </button>
        </div>
      )}

    </div>
  );
};

export default PublicOfferView;
